// @flow
import styled, { keyframes } from 'styled-components';

// ── Card shell ────────────────────────────────────────────────────────────────
export const SterlingCard = styled.div`
  position: relative;
  overflow: hidden;
  margin-top: 20px;
  border-radius: 20px;
  padding: 28px 28px 24px;
  color: #fff;
  background: linear-gradient(140deg, #1a0408 0%, #3d0c14 25%, #7b1525 58%, #c81e34 100%);
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.22),
    0 20px 56px rgba(200, 30, 52, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow:
      0 4px 8px rgba(0, 0, 0, 0.16),
      0 14px 32px rgba(0, 0, 0, 0.28),
      0 32px 72px rgba(200, 30, 52, 0.38),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  @media screen and (max-width: 620px) {
    padding: 22px 20px 20px;
  }
`;

export const DecorativeRingTop = styled.div`
  position: absolute;
  width: 260px;
  height: 260px;
  top: -80px;
  right: -80px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.07) 0%, transparent 70%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  pointer-events: none;
`;

export const DecorativeRingBottom = styled.div`
  position: absolute;
  width: 180px;
  height: 180px;
  bottom: -60px;
  left: -50px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%);
  pointer-events: none;
`;

// ── Header row ────────────────────────────────────────────────────────────────
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  position: relative;
  z-index: 1;
`;

export const CardBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const CardLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.14);
  padding: 5px;
`;

export const CardLogoFallback = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  font-size: 16px;
  opacity: 0.85;
`;

export const CardBankName = styled.span`
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  opacity: 0.88;
`;

export const CardDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;

  span {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.35);

    &:last-child {
      background: rgba(255, 255, 255, 0.65);
    }
  }
`;

// ── Balance section ───────────────────────────────────────────────────────────
export const BalanceSection = styled.div`
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

export const BalanceLabel = styled.p`
  font-size: 12px;
  opacity: 0.6;
  letter-spacing: 0.5px;
  margin: 0 0 6px;
  line-height: 1;
`;

export const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const BalanceAmount = styled.span`
  font-size: 38px;
  font-family: rebondG-Bold;
  line-height: 1;
  letter-spacing: -0.5px;

  @media screen and (max-width: 620px) {
    font-size: 28px;
  }
`;

// ── Footer row ────────────────────────────────────────────────────────────────
export const CardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 620px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const AccountInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AccountLabel = styled.p`
  font-size: 11px;
  opacity: 0.55;
  letter-spacing: 0.5px;
  margin: 0 0 5px;
  line-height: 1;
`;

export const AccountNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const AccountNumber = styled.span`
  font-size: 20px;
  font-family: rebondG-Bold;
  letter-spacing: 2px;
  line-height: 1;

  @media screen and (max-width: 620px) {
    font-size: 16px;
  }
`;

export const AccountHolder = styled.p`
  font-size: 11px;
  opacity: 0.6;
  margin: 6px 0 0;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  line-height: 1;
`;

// ── Buttons ───────────────────────────────────────────────────────────────────
export const IconToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 14px;
  padding: 0;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }

  &:active {
    transform: scale(0.92);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const RefreshButton = styled(IconToggleButton)`
  svg {
    animation: ${({ $spinning }) => ($spinning ? spin : "none")} 0.8s linear infinite;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:disabled:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const TransferButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 26px;
  border-radius: 50px;
  background: #fff;
  border: none;
  color: #c81e34;
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(0, 0, 0, 0.14);
  }

  &:active {
    transform: scale(0.97);
  }

  @media screen and (max-width: 620px) {
    width: 100%;
    justify-content: center;
    padding: 12px 0;
  }
`;

// ── Transfer Modal ─────────────────────────────────────────────────────────────
export const TransferOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 10, 20, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media screen and (min-width: 640px) {
    align-items: center;
  }
`;

export const TransferSheet = styled.div`
  background: #fff;
  width: 100%;
  max-width: 480px;
  border-radius: 24px 24px 0 0;
  max-height: 94dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  -webkit-overflow-scrolling: touch;

  @keyframes slideUp {
    from { transform: translateY(60px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  @media screen and (min-width: 640px) {
    border-radius: 24px;
    max-height: 88vh;
    animation: fadeScale 0.22s ease;

    @keyframes fadeScale {
      from { transform: scale(0.96); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
  }

  &::-webkit-scrollbar { width: 0; }
`;

export const TransferHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #e2e8f0;
  margin: 12px auto 0;
  flex-shrink: 0;

  @media screen and (min-width: 640px) {
    display: none;
  }
`;

export const TransferHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

export const TransferHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const TransferTitle = styled.h6`
  font-size: 17px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0;
`;

export const TransferCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;

  &:hover { background: #e2e8f0; }
`;

export const TransferBalancePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(200, 30, 52, 0.07);
  border: 1px solid rgba(200, 30, 52, 0.15);
  border-radius: 50px;
  padding: 5px 12px;
  margin-top: 10px;

  span:first-child {
    font-size: 11px;
    color: #64748b;
    letter-spacing: 0.3px;
  }

  span:last-child {
    font-size: 13px;
    font-family: rebondG-Semi-Bold;
    color: #c81e34;
  }
`;

export const TransferBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const PaymentTypeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const PaymentTypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 14px 14px 12px;
  border-radius: 14px;
  border: 2px solid ${({ $active }) => $active ? '#c81e34' : '#e2e8f0'};
  background: ${({ $active }) => $active ? 'rgba(200, 30, 52, 0.04)' : '#fafbfd'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    border-color: ${({ $active }) => $active ? '#c81e34' : '#cbd5e1'};
  }
`;

export const PaymentTypeIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${({ $active }) => $active ? 'rgba(200, 30, 52, 0.1)' : '#f1f5f9'};
  color: ${({ $active }) => $active ? '#c81e34' : '#94a3b8'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.15s ease;
`;

export const PaymentTypeName = styled.span`
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  color: ${({ $active }) => $active ? '#c81e34' : '#475569'};
  line-height: 1.3;
  transition: color 0.15s ease;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  color: #475569;
  letter-spacing: 0.3px;
`;

export const FieldInput = styled.input`
  height: 50px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 16px;
  font-size: 15px;
  color: #0f172a;
  background: #fafbfd;
  outline: none;
  width: 100%;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: #c81e34;
    background: #fff;
  }

  &::placeholder { color: #b0bac9; }
`;

export const FieldSelect = styled.select`
  height: 50px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 16px;
  font-size: 14px;
  color: #0f172a;
  background: #fafbfd;
  outline: none;
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:focus { border-color: #c81e34; background-color: #fff; }
`;

export const AmountInputWrapper = styled.div`
  position: relative;
`;

export const AmountPrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-family: rebondG-Semi-Bold;
  color: #475569;
  pointer-events: none;
`;

export const AmountInput = styled(FieldInput)`
  padding-left: 36px;
  font-size: 22px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  height: 58px;

  &::placeholder { font-size: 16px; font-family: rebondG-Regular, sans-serif; }

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const FieldHint = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
`;

export const TransferFooter = styled.div`
  padding: 16px 24px max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  background: #fff;
  position: sticky;
  bottom: 0;
`;

export const SendButton = styled.button`
  width: 100%;
  height: 54px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #9b1424 0%, #c81e34 100%);
  color: #fff;
  font-size: 15px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(200, 30, 52, 0.35);
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 22px rgba(200, 30, 52, 0.45);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

export const CancelTextBtn = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: #f8fafc; border-color: #cbd5e1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Ghost / Skeleton loader ───────────────────────────────────────────────────
const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
`;

export const SkeletonBase = styled.div`
  ${shimmer}
  border-radius: 10px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf4 50%, #f1f5f9 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
`;

export const SkeletonLine = styled(SkeletonBase)`
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  margin-bottom: ${({ $mb }) => $mb || '0'};
`;

export const SkeletonBlock = styled(SkeletonBase)`
  height: ${({ $h }) => $h || '50px'};
  width: 100%;
  border-radius: 12px;
`;

export const SkeletonTypeCard = styled(SkeletonBase)`
  height: 80px;
  border-radius: 14px;
`;

export const SkeletonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ── Payment PIN Setup ─────────────────────────────────────────────────────────
export const PinSetupBody = styled.div`
  padding: 32px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

export const PinSetupIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(200, 30, 52, 0.08);
  border: 1.5px solid rgba(200, 30, 52, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c81e34;
  font-size: 26px;
  margin-bottom: 8px;
`;

export const PinSetupTitle = styled.h6`
  font-size: 18px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0 0 4px;
  text-align: center;
`;

export const PinSetupSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
  margin: 0 0 24px;
  max-width: 280px;
`;

export const PinFieldLabel = styled.p`
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  color: #475569;
  letter-spacing: 0.3px;
  margin: 0 0 8px;
  align-self: flex-start;
  width: 100%;
`;

export const PinBoxRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
  margin-bottom: 20px;
`;

export const PinBox = styled.input`
  width: 56px;
  height: 60px;
  border: 2px solid ${({ $filled }) => $filled ? '#c81e34' : '#e2e8f0'};
  border-radius: 14px;
  background: ${({ $filled }) => $filled ? 'rgba(200, 30, 52, 0.04)' : '#fafbfd'};
  text-align: center;
  font-size: 22px;
  font-family: rebondG-Bold;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;
  caret-color: transparent;
  -webkit-text-security: disc;

  &:focus {
    border-color: #c81e34;
    background: rgba(200, 30, 52, 0.04);
  }

  @media screen and (max-width: 380px) {
    width: 48px;
    height: 54px;
    font-size: 18px;
  }
`;

export const PinError = styled.p`
  font-size: 13px;
  color: #c81e34;
  background: rgba(200, 30, 52, 0.07);
  border: 1px solid rgba(200, 30, 52, 0.18);
  border-radius: 10px;
  padding: 10px 14px;
  margin: 0 0 8px;
  width: 100%;
  text-align: center;
  line-height: 1.5;
`;

export const PinDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 18px;
  cursor: pointer;
  color: #64748b;
  font-size: 13px;
  font-family: rebondG-Medium;
  margin: 0 auto 4px;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:active {
    background: rgba(200, 30, 52, 0.06);
    border-color: #c81e34;
    color: #c81e34;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

// ── Fetch error state ─────────────────────────────────────────────────────────
export const FetchErrorBody = styled.div`
  padding: 48px 28px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const FetchErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(200, 30, 52, 0.08);
  border: 1.5px solid rgba(200, 30, 52, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c81e34;
  font-size: 24px;
  margin-bottom: 4px;
`;

export const FetchErrorTitle = styled.h6`
  font-size: 16px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0;
  text-align: center;
`;

export const FetchErrorMsg = styled.p`
  font-size: 13px;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
`;

// ── Recipient verify row ──────────────────────────────────────────────────────
export const RecipientRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

export const RecipientInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

export const VerifyBtn = styled.button`
  height: 50px;
  padding: 0 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #9b1424 0%, #c81e34 100%);
  color: #fff;
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(200, 30, 52, 0.28);
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    box-shadow: 0 5px 16px rgba(200, 30, 52, 0.38);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const VerifiedNameBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  border: 1.5px solid #22c55e;
  border-radius: 12px;
  padding: 0 14px;
  background: rgba(34, 197, 94, 0.05);
  margin-top: 8px;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

export const VerifiedCheckIcon = styled.span`
  color: #22c55e;
  font-size: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

export const VerifiedName = styled.span`
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VerifyErrorHint = styled.p`
  font-size: 12px;
  color: #c81e34;
  margin: 6px 0 0;
  line-height: 1.4;
`;

export const RetryButton = styled.button`
  margin-top: 8px;
  height: 46px;
  padding: 0 32px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #9b1424 0%, #c81e34 100%);
  color: #fff;
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(200, 30, 52, 0.3);
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    box-shadow: 0 6px 20px rgba(200, 30, 52, 0.4);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

// ── Transfer review screen ────────────────────────────────────────────────────
export const ReviewBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ReviewRecipientBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

export const ReviewRecipientAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9b1424 0%, #c81e34 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-family: rebondG-Bold;
  flex-shrink: 0;
`;

export const ReviewRecipientInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ReviewRecipientName = styled.p`
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ReviewRecipientSub = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
`;

export const ReviewBreakdownCard = styled.div`
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

export const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  background: #fff;
  border-bottom: 1px solid ${({ $last }) => $last ? 'transparent' : '#f1f5f9'};
`;

export const ReviewRowLabel = styled.span`
  font-size: 13px;
  color: #64748b;
`;

export const ReviewRowValue = styled.span`
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  color: ${({ $red }) => $red ? '#c81e34' : '#0f172a'};
`;

export const ReviewTotalRow = styled(ReviewRow)`
  background: rgba(200, 30, 52, 0.04);
  border-bottom: none;
`;

export const ReviewTotalLabel = styled.span`
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
`;

export const ReviewTotalValue = styled.span`
  font-size: 18px;
  font-family: rebondG-Bold;
  color: #c81e34;
`;

export const ReviewNote = styled.p`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

export const ReviewSkeletonBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ── Payment success screen ─────────────────────────────────────────────────────
const successPop = keyframes`
  0%   { transform: scale(0.4); opacity: 0; }
  65%  { transform: scale(1.15); opacity: 1; }
  80%  { transform: scale(0.95); }
  100% { transform: scale(1); }
`;

const successFadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const successRipple = keyframes`
  0%   { transform: scale(1); opacity: 0.35; }
  100% { transform: scale(1.9); opacity: 0; }
`;

export const SuccessBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 24px;
  overflow-y: auto;
`;

export const SuccessIconWrap = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  margin-bottom: 28px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(16, 185, 129, 0.18);
    animation: ${successRipple} 1.6s ease-out infinite;
  }

  &::after {
    animation-delay: 0.55s;
  }
`;

export const SuccessIconCircle = styled.div`
  position: relative;
  z-index: 1;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(145deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.38), 0 2px 8px rgba(16, 185, 129, 0.22);
  animation: ${successPop} 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;

export const SuccessTitle = styled.h2`
  font-family: rebondG-Bold;
  font-size: 22px;
  color: #0f172a;
  margin: 0 0 6px;
  text-align: center;
  animation: ${successFadeUp} 0.4s ease 0.25s both;
`;

export const SuccessSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 28px;
  text-align: center;
  line-height: 1.5;
  animation: ${successFadeUp} 0.4s ease 0.35s both;
`;

export const SuccessAmountBadge = styled.div`
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border: 1.5px solid #bbf7d0;
  border-radius: 20px;
  padding: 14px 32px;
  margin-bottom: 24px;
  animation: ${successFadeUp} 0.4s ease 0.4s both;

  span {
    font-family: rebondG-Bold;
    font-size: 28px;
    color: #059669;
    letter-spacing: -0.5px;
  }
`;

export const SuccessDetailCard = styled.div`
  width: 100%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 8px;
  animation: ${successFadeUp} 0.4s ease 0.5s both;
`;

export const SuccessDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

export const SuccessDetailLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
  font-family: rebondG-Regular;
`;

export const SuccessDetailValue = styled.span`
  font-size: 13px;
  color: #0f172a;
  font-family: rebondG-Semi-Bold;
  text-align: right;
  max-width: 60%;
  word-break: break-all;
`;

export const SuccessDoneBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #c81e34, #a0162a);
  color: #fff;
  font-family: rebondG-Semi-Bold;
  font-size: 16px;
  cursor: pointer;
  margin-top: 8px;
  box-shadow: 0 4px 16px rgba(200, 30, 52, 0.3);
  transition: opacity 0.15s ease, transform 0.12s ease;
  animation: ${successFadeUp} 0.4s ease 0.6s both;

  &:active {
    opacity: 0.88;
    transform: scale(0.98);
  }
`;

// ── Sterling wallet section tabs ───────────────────────────────────────────────
export const SterlingSection = styled.div`
  margin-top: 0;
`;

export const SterlingTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #f1f5f9;
  margin: 16px 0 0;
`;

export const SterlingTab = styled.button`
  flex: 1;
  padding: 12px 0;
  background: none;
  border: none;
  border-bottom: 2.5px solid ${({ $active }) => $active ? '#c81e34' : 'transparent'};
  margin-bottom: -2px;
  color: ${({ $active }) => $active ? '#c81e34' : '#94a3b8'};
  font-family: ${({ $active }) => $active ? 'rebondG-Semi-Bold' : 'rebondG-Regular'};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${({ $active }) => $active ? '#c81e34' : '#475569'};
  }
`;

export const SterlingTabPanel = styled.div`
  padding: 0;
  min-height: 120px;
`;

// ── Transaction history list ───────────────────────────────────────────────────
export const HistoryContainer = styled.div`
  margin-top: 4px;
`;

export const HistoryLoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 0;
`;

const historyShimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const HistorySkeleton = styled.div`
  height: ${({ $h }) => $h || '56px'};
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: ${historyShimmer} 1.4s infinite linear;
`;

export const HistoryEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
`;

export const HistoryEmptyIcon = styled.div`
  font-size: 36px;
  color: #e2e8f0;
`;

export const HistoryEmptyText = styled.p`
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
  text-align: center;
`;

export const HistoryErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  gap: 10px;
`;

export const HistoryErrorText = styled.p`
  font-size: 13px;
  color: #c81e34;
  text-align: center;
  margin: 0;
`;

export const HistoryRetryBtn = styled.button`
  background: none;
  border: 1.5px solid #c81e34;
  border-radius: 10px;
  padding: 8px 20px;
  color: #c81e34;
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;

  &:hover { background: rgba(200,30,52,0.05); }
`;

export const TxnDateGroup = styled.div`
  margin-bottom: 4px;
`;

export const TxnDateLabel = styled.p`
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 12px 0 6px;
  margin: 0;
`;

export const TxnCard = styled.div`
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 8px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  transition: box-shadow 0.15s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 3px 12px rgba(0,0,0,0.08);
  }
`;

export const TxnIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: ${({ $type }) => $type === 'credit'
    ? 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
    : 'linear-gradient(135deg, #fee2e2, #fecaca)'};
  color: ${({ $type }) => $type === 'credit' ? '#059669' : '#c81e34'};
`;

export const TxnMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TxnNarration = styled.p`
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0 0 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TxnSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const TxnTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

export const TxnChannel = styled.span`
  font-size: 10px;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 6px;
  padding: 2px 7px;
  font-family: rebondG-Regular;
`;

const statusColor = (s = "") => {
  const v = s.toLowerCase();
  if (v === 'successful' || v === 'success') return { bg: 'rgba(16,185,129,0.1)',  text: '#059669' };
  if (v === 'pending')                        return { bg: 'rgba(245,158,11,0.12)', text: '#b45309' };
  if (v === 'initiated')                      return { bg: 'rgba(47,103,216,0.10)', text: '#2f67d8' };
  if (v === 'processing')                     return { bg: 'rgba(124,58,237,0.10)', text: '#7c3aed' };
  /* failed + anything else */               return { bg: 'rgba(200,30,52,0.10)',   text: '#c81e34' };
};

export const TxnStatusBadge = styled.span`
  font-size: 10px;
  border-radius: 6px;
  padding: 2px 7px;
  font-family: rebondG-Semi-Bold;
  background: ${({ $status }) => statusColor($status).bg};
  color: ${({ $status }) => statusColor($status).text};
`;

export const TxnAmountCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  flex-shrink: 0;
`;

export const TxnAmount = styled.span`
  font-size: 15px;
  font-family: rebondG-Bold;
  color: ${({ $type }) => $type === 'credit' ? '#059669' : '#c81e34'};
`;

export const TxnBalance = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

export const TxnRef = styled.span`
  font-size: 10px;
  color: #b0bec5;
  font-family: rebondG-Regular;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
`;

// ── Transaction detail drawer ──────────────────────────────────────────────────
export const TxnDrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  backdrop-filter: blur(2px);
`;

export const TxnDrawerSheet = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  border-radius: 24px 24px 0 0;
  padding: 0 0 32px;
  max-height: 88vh;
  overflow-y: auto;
`;

export const TxnDrawerHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 12px auto 0;
`;

export const TxnDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
`;

export const TxnDrawerTitle = styled.h3`
  font-size: 15px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0;
`;

export const TxnDrawerClose = styled.button`
  background: #f1f5f9;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;

  &:hover { background: #e2e8f0; }
`;

export const TxnDrawerAmountBanner = styled.div`
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #f1f5f9;
`;

export const TxnDrawerAmountType = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 4px;
  background: ${({ $type }) => $type === 'credit'
    ? 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
    : 'linear-gradient(135deg, #fee2e2, #fecaca)'};
  color: ${({ $type }) => $type === 'credit' ? '#059669' : '#c81e34'};
`;

export const TxnDrawerBigAmount = styled.p`
  font-size: 28px;
  font-family: rebondG-Bold;
  margin: 0;
  color: ${({ $type }) => $type === 'credit' ? '#059669' : '#c81e34'};
`;

export const TxnDrawerAmountLabel = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
`;

export const TxnDrawerBody = styled.div`
  padding: 16px 20px;
`;

export const TxnDetailSection = styled.div`
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
`;

export const TxnDetailSectionTitle = styled.p`
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
  margin: 0;
  padding: 10px 14px 6px;
`;

export const TxnDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 14px;
  border-top: 1px solid #f1f5f9;
  gap: 12px;
`;

export const TxnDetailLabel = styled.span`
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
`;

export const TxnDetailValue = styled.span`
  font-size: 12px;
  color: #0f172a;
  font-family: rebondG-Semi-Bold;
  text-align: right;
  word-break: break-all;
`;

export const TxnDrawerStatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 20px;
`;

// ── History pagination ─────────────────────────────────────────────────────────
export const HistoryPaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 4px;
  gap: 8px;
`;

export const HistoryPageBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #c81e34;
    color: #c81e34;
  }

  &:disabled {
    opacity: 0.38;
    cursor: default;
  }
`;

export const HistoryPageInfo = styled.span`
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
`;

// ── Searchable bank select ─────────────────────────────────────────────────────
const dropIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export const BankSelectWrap = styled.div`
  position: relative;
  width: 100%;
`;

export const BankSelectTrigger = styled.button`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: #fff;
  border: 1.5px solid ${({ $open }) => $open ? '#c81e34' : '#e2e8f0'};
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  box-shadow: ${({ $open }) => $open ? '0 0 0 3px rgba(200,30,52,0.08)' : 'none'};

  &:hover:not(:disabled) {
    border-color: #c81e34;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const BankSelectValue = styled.span`
  font-size: 14px;
  font-family: rebondG-Medium;
  color: ${({ $hasValue }) => $hasValue ? '#0f172a' : '#b0bec5'};
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const BankSelectChevron = styled.span`
  flex-shrink: 0;
  color: #94a3b8;
  margin-left: 10px;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
`;

export const BankDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 16px;
  box-shadow:
    0 4px 6px rgba(0,0,0,0.04),
    0 20px 48px rgba(0,0,0,0.12);
  z-index: 9999;
  overflow: hidden;
  animation: ${dropIn} 0.18s cubic-bezier(0.22,1,0.36,1) both;
`;

export const BankSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbff;
`;

export const BankSearchIcon = styled.span`
  color: #94a3b8;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

export const BankSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-family: rebondG-Medium;
  color: #0f172a;

  &::placeholder {
    color: #c7d0dc;
    font-family: rebondG-Regular;
  }
`;

export const BankSearchClearBtn = styled.button`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.12s ease;

  &:hover {
    background: #cbd5e1;
  }
`;

export const BankOptionList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
`;

export const BankOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  border: none;
  background: ${({ $selected }) => $selected ? 'rgba(200,30,52,0.04)' : 'transparent'};
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
  border-bottom: 1px solid #f8fafc;

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${({ $selected }) => $selected ? 'rgba(200,30,52,0.07)' : '#f8fafc'};
  }
`;

export const BankOptionName = styled.span`
  font-size: 13px;
  font-family: ${({ $selected }) => $selected ? 'rebondG-Semi-Bold' : 'rebondG-Medium'};
  color: ${({ $selected }) => $selected ? '#c81e34' : '#0f172a'};
  flex: 1;

  mark {
    background: rgba(200,30,52,0.12);
    color: #c81e34;
    border-radius: 2px;
    padding: 0 1px;
  }
`;

export const BankOptionCheck = styled.span`
  flex-shrink: 0;
  color: #c81e34;
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

export const BankNoResult = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  font-family: rebondG-Regular;
`;
